import { useState, useEffect } from "react";
import { CalendarCheck, CheckCircle, XCircle, User, CreditCard, Settings, RefreshCw, Edit3, PlusCircle, MinusCircle } from "lucide-react";
import { clsx } from "clsx";
import { Modal } from "../../../components/ui/Modal";
import { Badge, statusVariant } from "../../../components/ui/Badge";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { ToastContainer } from "../../../components/ui/Toast";
import { useToast } from "../../../hooks/useToast";
import { useBizLeaves, useBizApproveLeave, useBizRejectLeave } from "../../../hooks/useBizAdmin";
import { useAuth } from "../../../hooks/useAuth";
import type { Leave, LeaveBalance } from "../../../lib/leaveApi";
import { formatDate } from "../../../utils/format";
import { ApiError } from "../../../lib/api";
import {
  useLeavePolicy,
  useSaveLeavePolicy,
  useLeaveBalances,
  useSetLeaveBalance,
  useAdjustLeaveBalance,
  useBulkInitLeaveBalances,
} from "../../../hooks/useLeave";

// ── Constants ────────────────────────────────────────────────────────────────

const LEAVE_TYPE_LABELS: Record<string, string> = {
  VACATION: "Vacation Leave",
  SICK: "Sick Leave",
  EMERGENCY: "Emergency Leave",
  MATERNITY: "Maternity Leave",
  PATERNITY: "Paternity Leave",
};

const LEAVE_TYPE_COLORS: Record<string, string> = {
  VACATION: "bg-[#eff3ff] text-[#2447d9]",
  SICK: "bg-[#f0fdf4] text-[#16a34a]",
  EMERGENCY: "bg-[#fef2f2] text-[#dc2626]",
  MATERNITY: "bg-[#f5f3ff] text-[#7c3aed]",
  PATERNITY: "bg-[#ecfeff] text-[#0891b2]",
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDateRange(start: string, end: string): string {
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const startStr = s.toLocaleDateString("en-US", opts);
  const endStr = e.toLocaleDateString("en-US", { ...opts, year: "numeric" });
  return `${startStr} – ${endStr}`;
}

function displayName(leave: Leave): string {
  return leave.employeeName ?? `Employee (${leave.userId.slice(0, 8)}…)`;
}

// ── Leave Detail Modal ────────────────────────────────────────────────────────

interface DetailModalProps {
  leave: Leave;
  onClose: () => void;
  onApprove: (leaveId: string, userId: string, note?: string) => Promise<void>;
  onReject: (leaveId: string, userId: string, note: string) => Promise<void>;
  isSubmitting: boolean;
}

function LeaveDetailModal({ leave, onClose, onApprove, onReject, isSubmitting }: DetailModalProps) {
  const [note, setNote] = useState("");
  const [noteError, setNoteError] = useState<string | null>(null);
  const [action, setAction] = useState<"APPROVE" | "REJECT" | null>(null);

  const { canApproveAction } = useAuth();
  const canApprove = canApproveAction("leave", "approve");
  const canReject = canApproveAction("leave", "reject");

  const isPending = leave.status === "PENDING";

  const handleAction = async (a: "APPROVE" | "REJECT") => {
    if (a === "REJECT" && !note.trim()) {
      setNoteError("Rejection reason is required.");
      return;
    }
    setNoteError(null);
    if (a === "APPROVE") {
      await onApprove(leave.leaveId, leave.userId, note.trim() || undefined);
    } else {
      await onReject(leave.leaveId, leave.userId, note.trim());
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Leave Request Details" size="md">
      <div className="space-y-5">
        {/* Employee info */}
        <div className="flex items-center gap-3 rounded-xl bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#eff3ff]">
            <User className="h-5 w-5 text-[#2447d9]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#0f172a]">{displayName(leave)}</p>
            <p className="text-xs text-[#64748b]">Applied {formatDate(leave.createdAt)}</p>
          </div>
          <div className="ml-auto">
            <Badge variant={statusVariant(leave.status)}>{leave.status}</Badge>
          </div>
        </div>

        {/* Leave details grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3">
            <p className="text-xs text-[#94a3b8] mb-1">Leave Type</p>
            <span
              className={clsx(
                "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                LEAVE_TYPE_COLORS[leave.type] ?? "bg-[#f1f5f9] text-[#64748b]",
              )}
            >
              {LEAVE_TYPE_LABELS[leave.type] ?? leave.type}
            </span>
          </div>

          <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3">
            <p className="text-xs text-[#94a3b8] mb-1">Duration</p>
            <p className="text-sm font-semibold text-[#0f172a]">
              {leave.days} working {leave.days === 1 ? "day" : "days"}
            </p>
          </div>

          <div className="col-span-2 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3">
            <p className="text-xs text-[#94a3b8] mb-1">Date Range</p>
            <p className="text-sm font-semibold text-[#0f172a]">
              {formatDateRange(leave.startDate, leave.endDate)}
            </p>
          </div>

          <div className="col-span-2 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3">
            <p className="text-xs text-[#94a3b8] mb-1">Reason</p>
            <p className="text-sm text-[#0f172a]">{leave.reason}</p>
          </div>

          {!isPending && leave.reviewedBy && (
            <div className="col-span-2 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3">
              <p className="text-xs text-[#94a3b8] mb-1">Review Note</p>
              <p className="text-sm text-[#0f172a]">{leave.reviewNote ?? "—"}</p>
              <p className="text-xs text-[#64748b] mt-1">
                by {leave.reviewedBy}{leave.reviewedAt ? ` · ${formatDate(leave.reviewedAt)}` : ""}
              </p>
            </div>
          )}
        </div>

        {/* Audit trail */}
        <div className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3 space-y-2">
          <p className="text-xs font-semibold text-[#374151] uppercase tracking-wide">Audit Trail</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <div>
              <p className="text-[#94a3b8]">Applied On</p>
              <p className="font-medium text-[#374151]">{formatDate(leave.createdAt)}</p>
            </div>
            <div>
              <p className="text-[#94a3b8]">Last Updated</p>
              <p className="font-medium text-[#374151]">{leave.updatedAt ? formatDate(leave.updatedAt) : "—"}</p>
            </div>
            {leave.reviewedBy && (
              <div>
                <p className="text-[#94a3b8]">Reviewed By</p>
                <p className="font-medium text-[#374151]">{leave.reviewedBy}</p>
              </div>
            )}
            {leave.reviewedAt && (
              <div>
                <p className="text-[#94a3b8]">Reviewed On</p>
                <p className="font-medium text-[#374151]">{formatDate(leave.reviewedAt)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Approve / Reject section — only for pending */}
        {isPending && (
          <div className="border-t border-[#e2e8f0] pt-4 space-y-3">
            <p className="text-sm font-medium text-[#0f172a]">Review this request</p>

            <div>
              <label className="block text-xs font-medium text-[#64748b] mb-1.5">
                Note{action === "REJECT" ? " (required)" : " (optional)"}
              </label>
              <textarea
                value={note}
                onChange={(e) => { setNote(e.target.value); if (noteError) setNoteError(null); }}
                rows={3}
                maxLength={500}
                placeholder="Add a note for the employee…"
                className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
              />
              {noteError && <p className="text-xs text-[#dc2626] mt-1">{noteError}</p>}
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                disabled={isSubmitting || !canApprove}
                title={!canApprove ? "You don't have permission to approve" : undefined}
                onClick={() => { setAction("APPROVE"); void handleAction("APPROVE"); }}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#16a34a] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#15803d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckCircle className="h-4 w-4" />
                {isSubmitting && action === "APPROVE" ? "Approving…" : "Approve"}
              </button>
              <button
                type="button"
                disabled={isSubmitting || !canReject}
                title={!canReject ? "You don't have permission to reject" : undefined}
                onClick={() => { setAction("REJECT"); void handleAction("REJECT"); }}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-[#dc2626] px-4 py-2.5 text-sm font-semibold text-[#dc2626] hover:bg-[#fef2f2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <XCircle className="h-4 w-4" />
                {isSubmitting && action === "REJECT" ? "Rejecting…" : "Reject"}
              </button>
            </div>
            {!canApprove && !canReject && (
              <p className="text-xs text-[#94a3b8]">You don't have permission to review leave requests.</p>
            )}
          </div>
        )}

        {/* Close button for reviewed requests */}
        {!isPending && (
          <div className="flex justify-end border-t border-[#e2e8f0] pt-4">
            <button
              onClick={onClose}
              className="rounded-lg border border-[#e2e8f0] px-4 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Set Balance Modal ─────────────────────────────────────────────────────────

interface SetBalanceModalProps {
  balance: LeaveBalance;
  businessId: string;
  onClose: () => void;
  onSuccess: (msg: string) => void;
}

function SetBalanceModal({ balance, businessId, onClose, onSuccess }: SetBalanceModalProps) {
  const [total, setTotal] = useState(String(balance.totalCredits));
  const [used, setUsed] = useState(String(balance.usedCredits));
  const setBalance = useSetLeaveBalance();

  const handleSave = async () => {
    const t = parseInt(total, 10);
    const u = parseInt(used, 10);
    if (isNaN(t) || isNaN(u)) return;
    await setBalance.mutateAsync({
      businessId,
      userId: balance.userId,
      payload: {
        year: balance.year,
        totalCredits: t,
        usedCredits: u,
        employeeName: balance.employeeName,
      },
    });
    onSuccess("Balance updated.");
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} title={`Set Balance — ${balance.employeeName}`} size="sm">
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Total Credits</label>
          <input
            type="number"
            min={0}
            max={365}
            value={total}
            onChange={(e) => setTotal(e.target.value)}
            className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Used Credits</label>
          <input
            type="number"
            min={0}
            max={365}
            value={used}
            onChange={(e) => setUsed(e.target.value)}
            className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
          />
        </div>
        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-[#e2e8f0] px-4 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={setBalance.isPending}
            onClick={() => void handleSave()}
            className="flex-1 rounded-lg bg-[#2447d9] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors disabled:opacity-60"
          >
            {setBalance.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Adjust Balance Modal ──────────────────────────────────────────────────────

interface AdjustModalProps {
  balance: LeaveBalance;
  businessId: string;
  year: number;
  onClose: () => void;
  onSuccess: (msg: string) => void;
}

function AdjustBalanceModal({ balance, businessId, year, onClose, onSuccess }: AdjustModalProps) {
  const [sign, setSign] = useState<"+" | "-">("+");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const adjust = useAdjustLeaveBalance();

  const handleSave = async () => {
    const n = parseInt(amount, 10);
    if (isNaN(n) || n <= 0) { setError("Enter a positive number."); return; }
    if (!reason.trim()) { setError("Reason is required."); return; }
    setError(null);
    const delta = sign === "+" ? n : -n;
    await adjust.mutateAsync({
      businessId,
      userId: balance.userId,
      payload: { delta, reason: reason.trim(), year },
    });
    onSuccess(`Balance adjusted by ${sign}${n}.`);
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} title={`Adjust Balance — ${balance.employeeName}`} size="sm">
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Adjustment</label>
          <div className="flex gap-2 items-center">
            <button
              type="button"
              onClick={() => setSign("+")}
              className={clsx(
                "flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold border transition-colors",
                sign === "+"
                  ? "bg-[#dcfce7] border-[#16a34a] text-[#15803d]"
                  : "bg-white border-[#e2e8f0] text-[#64748b]",
              )}
            >
              <PlusCircle className="h-4 w-4" /> Add
            </button>
            <button
              type="button"
              onClick={() => setSign("-")}
              className={clsx(
                "flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold border transition-colors",
                sign === "-"
                  ? "bg-[#fee2e2] border-[#dc2626] text-[#b91c1c]"
                  : "bg-white border-[#e2e8f0] text-[#64748b]",
              )}
            >
              <MinusCircle className="h-4 w-4" /> Subtract
            </button>
            <input
              type="number"
              min={1}
              max={365}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Days"
              className="flex-1 rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Reason <span className="text-[#dc2626]">*</span></label>
          <textarea
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(e) => { setReason(e.target.value); if (error) setError(null); }}
            placeholder="e.g. Annual leave carry-over"
            className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
          />
          {error && <p className="text-xs text-[#dc2626] mt-1">{error}</p>}
        </div>
        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-[#e2e8f0] px-4 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors">Cancel</button>
          <button
            type="button"
            disabled={adjust.isPending}
            onClick={() => void handleSave()}
            className="flex-1 rounded-lg bg-[#2447d9] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors disabled:opacity-60"
          >
            {adjust.isPending ? "Saving…" : "Save Adjustment"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Leave Credits Tab ─────────────────────────────────────────────────────────

interface LeaveCreditsTabProps {
  businessId: string;
  toast: { success: (msg: string) => void; error: (msg: string) => void };
}

function LeaveCreditsTab({ businessId, toast }: LeaveCreditsTabProps) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [policyInput, setPolicyInput] = useState<string>("");
  const [confirmInit, setConfirmInit] = useState(false);
  const [setBalanceFor, setSetBalanceFor] = useState<LeaveBalance | null>(null);
  const [adjustFor, setAdjustFor] = useState<LeaveBalance | null>(null);

  const policyQuery = useLeavePolicy(businessId);
  const savePolicy = useSaveLeavePolicy();
  const balancesQuery = useLeaveBalances(businessId, year);
  const bulkInit = useBulkInitLeaveBalances();

  const policy = policyQuery.data?.policy;
  const balances = balancesQuery.data?.balances ?? [];

  useEffect(() => {
    if (policy !== undefined) {
      setPolicyInput(String(policy?.creditsPerYear ?? 0));
    }
  }, [policy]);

  const handleSavePolicy = async () => {
    const n = parseInt(policyInput, 10);
    if (isNaN(n) || n < 0 || n > 365) {
      toast.error("Enter a valid number between 0 and 365.");
      return;
    }
    try {
      await savePolicy.mutateAsync({ businessId, creditsPerYear: n });
      toast.success("Leave policy saved.");
    } catch {
      toast.error("Failed to save policy.");
    }
  };

  const handleBulkInit = async () => {
    setConfirmInit(false);
    try {
      const res = await bulkInit.mutateAsync({ businessId, year });
      toast.success(`Initialized ${res.initialized} employee(s). ${res.skipped} skipped (already set).`);
    } catch {
      toast.error("Failed to initialize balances.");
    }
  };

  const years = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <div className="space-y-5">
      {/* Policy card */}
      <div className="rounded-xl border border-[#e2e8f0] bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Settings className="h-4 w-4 text-[#2447d9]" />
          <h2 className="text-sm font-semibold text-[#0f172a]">Leave Policy</h2>
        </div>
        <div className="flex items-end gap-3">
          <div className="flex-1 max-w-xs">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Credits per Year
            </label>
            <input
              type="number"
              min={0}
              max={365}
              value={policyInput}
              onChange={(e) => setPolicyInput(e.target.value)}
              className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
            />
          </div>
          <button
            type="button"
            disabled={savePolicy.isPending}
            onClick={() => void handleSavePolicy()}
            className="rounded-lg bg-[#2447d9] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors disabled:opacity-60"
          >
            {savePolicy.isPending ? "Saving…" : "Save"}
          </button>
        </div>
        {policy && (
          <p className="mt-2 text-xs text-[#64748b]">
            Last updated: {formatDate(policy.updatedAt ?? "")} by {policy.updatedBy ?? "—"}
          </p>
        )}
      </div>

      {/* Year selector + bulk init */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-[#374151]">Year:</label>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#0f172a] focus:border-[#2447d9] focus:outline-none"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={() => setConfirmInit(true)}
          disabled={bulkInit.isPending}
          className="flex items-center gap-1.5 rounded-lg border border-[#2447d9] px-4 py-2 text-sm font-medium text-[#2447d9] hover:bg-[#eff3ff] transition-colors disabled:opacity-60"
        >
          <RefreshCw className="h-4 w-4" />
          Initialize {year} for All Employees
        </button>
      </div>

      {/* Confirm init modal */}
      {confirmInit && (
        <Modal isOpen onClose={() => setConfirmInit(false)} title="Confirm Initialization" size="sm">
          <div className="space-y-4">
            <p className="text-sm text-[#374151]">
              This will create leave balance records for all <strong>active</strong> employees in <strong>{year}</strong> who do not already have one. Existing balances will not be changed.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmInit(false)}
                className="flex-1 rounded-lg border border-[#e2e8f0] px-4 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={bulkInit.isPending}
                onClick={() => void handleBulkInit()}
                className="flex-1 rounded-lg bg-[#2447d9] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors disabled:opacity-60"
              >
                {bulkInit.isPending ? "Initializing…" : "Proceed"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Balances table */}
      {balancesQuery.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" className="text-[#2447d9]" />
        </div>
      ) : balances.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-[#e2e8f0] bg-white py-12 text-center">
          <CreditCard className="h-10 w-10 text-[#c7d2fe]" />
          <p className="text-sm font-medium text-[#0f172a]">No leave balances for {year}</p>
          <p className="text-xs text-[#94a3b8]">Click "Initialize" to create records for all active employees.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-[#e2e8f0] bg-white overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                  {["Employee", "Total", "Used", "Remaining", "Updated By", "Last Updated", "Actions"].map((h) => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#f1f5f9]">
                {balances.map((b) => {
                  const remaining = b.totalCredits - b.usedCredits;
                  return (
                    <tr key={b.userId} className="bg-white hover:bg-[#f8fafc] transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#eff3ff] text-xs font-bold text-[#2447d9]">
                            {(b.employeeName ?? "?")[0]?.toUpperCase()}
                          </div>
                          <p className="text-sm font-medium text-[#0f172a]">{b.employeeName}</p>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-[#374151]">{b.totalCredits}</td>
                      <td className="px-5 py-3.5 text-sm text-[#374151]">{b.usedCredits}</td>
                      <td className="px-5 py-3.5">
                        <span className={`text-sm font-semibold ${remaining > 0 ? "text-[#16a34a]" : "text-[#dc2626]"}`}>
                          {remaining}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-xs text-[#374151]">{b.updatedBy ?? "—"}</td>
                      <td className="px-5 py-3.5 text-xs text-[#64748b]">{formatDate(b.updatedAt)}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setSetBalanceFor(b)}
                            className="flex items-center gap-1 rounded-md border border-[#e2e8f0] px-2.5 py-1 text-xs font-medium text-[#374151] hover:bg-[#f1f5f9] transition-colors"
                          >
                            <Edit3 className="h-3.5 w-3.5" /> Set
                          </button>
                          <button
                            type="button"
                            onClick={() => setAdjustFor(b)}
                            className="flex items-center gap-1 rounded-md border border-[#e2e8f0] px-2.5 py-1 text-xs font-medium text-[#374151] hover:bg-[#f1f5f9] transition-colors"
                          >
                            <PlusCircle className="h-3.5 w-3.5" /> Adjust
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {setBalanceFor && (
        <SetBalanceModal
          balance={setBalanceFor}
          businessId={businessId}
          onClose={() => setSetBalanceFor(null)}
          onSuccess={(msg) => { toast.success(msg); void balancesQuery.refetch(); }}
        />
      )}
      {adjustFor && (
        <AdjustBalanceModal
          balance={adjustFor}
          businessId={businessId}
          year={year}
          onClose={() => setAdjustFor(null)}
          onSuccess={(msg) => { toast.success(msg); void balancesQuery.refetch(); }}
        />
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

type TabStatus = "ALL" | "PENDING" | "APPROVED" | "REJECTED" | "CREDITS";

const TABS: { label: string; value: TabStatus }[] = [
  { label: "All",            value: "ALL"      },
  { label: "Pending",        value: "PENDING"  },
  { label: "Approved",       value: "APPROVED" },
  { label: "Rejected",       value: "REJECTED" },
  { label: "Leave Credits",  value: "CREDITS"  },
];

export default function AdminLeavePage() {
  const [activeTab, setActiveTab] = useState<TabStatus>("PENDING");
  const [selectedLeave, setSelectedLeave] = useState<Leave | null>(null);
  const [knownCounts, setKnownCounts] = useState<Partial<Record<TabStatus, number>>>({});
  const { toasts, toast, dismissToast } = useToast();

  const { activeBusinessId } = useAuth();
  const bizId = activeBusinessId ?? "";

  const isCreditsTab = activeTab === "CREDITS";
  const statusFilter = (isCreditsTab || activeTab === "ALL") ? undefined : activeTab as "PENDING" | "APPROVED" | "REJECTED";
  const bizLeaves = useBizLeaves(bizId, statusFilter);
  const bizApprove = useBizApproveLeave(bizId);
  const bizReject = useBizRejectLeave(bizId);

  const isLoading = bizLeaves.isLoading;
  const isSubmitting = bizApprove.isPending || bizReject.isPending;
  const items: Leave[] = bizLeaves.data ?? [];

  useEffect(() => {
    if (!isLoading && bizLeaves.data !== undefined && !isCreditsTab) {
      setKnownCounts((prev) => ({ ...prev, [activeTab]: bizLeaves.data!.length }));
    }
  }, [isLoading, bizLeaves.data, activeTab, isCreditsTab]);

  const handleApprove = async (leaveId: string, userId: string, note?: string) => {
    try {
      await bizApprove.mutateAsync({ leaveId, userId, note });
      toast.success("Leave approved successfully.");
      setSelectedLeave(null);
    } catch (err) {
      if (err instanceof ApiError && err.code === "ALREADY_REVIEWED") {
        toast.error("This leave has already been reviewed.");
        setSelectedLeave(null);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    }
  };

  const handleReject = async (leaveId: string, userId: string, note: string) => {
    try {
      await bizReject.mutateAsync({ leaveId, userId, note });
      toast.success("Leave rejected.");
      setSelectedLeave(null);
    } catch (err) {
      if (err instanceof ApiError && err.code === "ALREADY_REVIEWED") {
        toast.error("This leave has already been reviewed.");
        setSelectedLeave(null);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    }
  };

  return (
    <div className="space-y-5">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* ── Header card ───────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-[#e2e8f0] bg-white px-5 py-4 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#2447d9]">
              <CalendarCheck className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-[#0f172a]">Manage Leave</h1>
              <p className="text-xs text-[#64748b]">Review and action employee leave requests</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tabs ──────────────────────────────────────────────────────────── */}
      <div className="flex gap-1 rounded-lg bg-[#f1f5f9] p-1 w-fit overflow-x-auto overflow-y-hidden">
        {TABS.map((tab) => {
          const count = knownCounts[tab.value];
          return (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={clsx(
                "flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap",
                activeTab === tab.value
                  ? "bg-white text-[#0f172a] shadow-sm"
                  : "text-[#64748b] hover:text-[#0f172a]",
              )}
            >
              {tab.label}
              {count !== undefined && count > 0 && (
                <span className={`text-xs rounded-full px-1.5 py-0.5 font-semibold ${
                  activeTab === tab.value
                    ? "bg-[#eff3ff] text-[#2447d9]"
                    : "bg-[#e2e8f0] text-[#64748b]"
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Content ───────────────────────────────────────────────────────── */}
      {isCreditsTab ? (
        !bizId ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-[#e2e8f0] bg-white py-16 text-center">
            <CreditCard className="h-10 w-10 text-[#c7d2fe]" />
            <p className="text-sm text-[#64748b]">Select a business to manage leave credits.</p>
          </div>
        ) : (
          <LeaveCreditsTab businessId={bizId} toast={toast} />
        )
      ) : !bizId ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-[#e2e8f0] bg-white py-16 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#eff3ff]">
            <CalendarCheck className="h-6 w-6 text-[#2447d9]" />
          </div>
          <p className="text-sm text-[#64748b]">Select a business to manage leave requests.</p>
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner size="lg" className="text-[#2447d9]" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-[#e2e8f0] bg-white py-16 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#eff3ff]">
            <CalendarCheck className="h-6 w-6 text-[#2447d9]" />
          </div>
          <p className="text-sm font-medium text-[#0f172a]">No leave requests</p>
          <p className="text-xs text-[#94a3b8]">
            {activeTab === "ALL"
              ? "No leave requests yet for this business."
              : `No ${activeTab.toLowerCase()} leave requests.`}
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-[#e2e8f0] bg-white overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[580px]">
              <thead>
                <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                  {["Employee", "Leave Type", "Date Range", "Applied On", "Status"].map((h) => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#f1f5f9]">
                {items.map((leave) => (
                  <tr
                    key={leave.leaveId}
                    onClick={() => setSelectedLeave(leave)}
                    className="bg-white hover:bg-[#f8fafc] cursor-pointer transition-colors group"
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#eff3ff] text-xs font-bold text-[#2447d9]">
                          {(leave.employeeName ?? "?")[0]?.toUpperCase()}
                        </div>
                        <p className="text-sm font-medium text-[#0f172a] group-hover:text-[#2447d9] transition-colors">
                          {displayName(leave)}
                        </p>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={clsx(
                        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                        LEAVE_TYPE_COLORS[leave.type] ?? "bg-[#f1f5f9] text-[#64748b]",
                      )}>
                        {LEAVE_TYPE_LABELS[leave.type] ?? leave.type}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-sm text-[#0f172a]">{formatDateRange(leave.startDate, leave.endDate)}</p>
                      <p className="text-xs text-[#94a3b8]">{leave.days} working {leave.days === 1 ? "day" : "days"}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-sm text-[#64748b]">{formatDate(leave.createdAt)}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      <Badge variant={statusVariant(leave.status)}>{leave.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {selectedLeave && (
        <LeaveDetailModal
          leave={selectedLeave}
          onClose={() => setSelectedLeave(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
}
